import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Calendar, CalendarPlus, ChevronLeft, ChevronRight, Download, ExternalLink, List, Mail, MapPin, Phone, RefreshCw, Search,
  UserX, Video, XCircle, CheckCircle2, AlertTriangle, Bell, Clock,
} from 'lucide-react';
import { usePageVisitLogger } from '../hooks/useActivityLogger';
import { interviewsV2, errorText, fmtIst, fmtIstTime, toIstParts, MODE_LABEL } from '../components/interviews/interviewApi';
import ScheduleInterviewDialog from '../components/interviews/ScheduleInterviewDialog';
import { StatusPill, ModePill, OutcomeDialog, CancelDialog } from '../components/interviews/InterviewActions';

/** Interviews v2 - today / upcoming / past / cancelled, a week calendar, filters, one-click actions. */
const VIEWS = [
  { key: 'today', label: 'Today' }, { key: 'upcoming', label: 'Upcoming' }, { key: 'past', label: 'Past' },
  { key: 'cancelled', label: 'Cancelled' }, { key: 'all', label: 'All' },
];
const DAY_MS = 86400000;

function startOfIstWeek(d) {
  // Monday 00:00 IST as a Date (UTC instant)
  const p = toIstParts(d.toISOString());
  const local = new Date(`${p.date}T00:00:00+05:30`);
  const dow = (local.getUTCDay() + 6) % 7; // Monday=0 in IST terms (offset keeps day boundary consistent)
  return new Date(local.getTime() - dow * DAY_MS);
}

export default function InterviewsPageV2() {
  usePageVisitLogger('Interviews');
  const [view, setView] = useState('upcoming');
  const [layout, setLayout] = useState('list');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [status, setStatus] = useState(null);
  const [q, setQ] = useState('');
  const [mode, setMode] = useState('all');
  const [recruiter, setRecruiter] = useState('all');
  const [users, setUsers] = useState([]);
  const [weekStart, setWeekStart] = useState(() => startOfIstWeek(new Date()));
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [pickCandidate, setPickCandidate] = useState('');
  const [reschedule, setReschedule] = useState(null);
  const [outcomeFor, setOutcomeFor] = useState(null);
  const [outcomePreset, setOutcomePreset] = useState('pending');
  const [cancelFor, setCancelFor] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (layout === 'week') {
        params.from_date = weekStart.toISOString();
        params.to_date = new Date(weekStart.getTime() + 7 * DAY_MS).toISOString();
      } else if (view === 'cancelled') params.status = 'cancelled';
      else if (view !== 'all') params.view = view;
      if (q.trim()) params.q = q.trim();
      if (mode !== 'all') params.mode = mode;
      if (recruiter !== 'all') params.recruiter_id = recruiter;
      const r = await interviewsV2.list(params);
      setRows(r.interviews || []);
      setTotal(r.total || 0);
    } catch (e) {
      toast.error(errorText(e.message, 'Could not load interviews'));
      setRows([]);
    } finally { setLoading(false); }
  }, [view, layout, weekStart, q, mode, recruiter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    interviewsV2.summary().then(setSummary).catch(() => {});
    interviewsV2.status().then(setStatus).catch(() => {});
    interviewsV2.users().then(setUsers).catch(() => {});
  }, []);
  const refresh = () => { load(); interviewsV2.summary().then(setSummary).catch(() => {}); };

  const byDay = useMemo(() => {
    const m = {};
    rows.forEach((iv) => { const d = toIstParts(iv.scheduled_at).date; (m[d] = m[d] || []).push(iv); });
    Object.values(m).forEach((arr) => arr.sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at)));
    return m;
  }, [rows]);

  // conflict warning within the loaded list (same candidate / interviewer / contact overlapping)
  const clashes = useMemo(() => {
    const ids = new Set();
    const open = rows.filter((r) => r.status === 'scheduled');
    for (let i = 0; i < open.length; i += 1) {
      for (let j = i + 1; j < open.length; j += 1) {
        const a = open[i]; const b = open[j];
        const aS = new Date(a.scheduled_at).getTime(); const aE = aS + (a.duration_minutes || 45) * 60000;
        const bS = new Date(b.scheduled_at).getTime(); const bE = bS + (b.duration_minutes || 45) * 60000;
        if (!(aS < bE && bS < aE)) continue;
        const shared = a.candidate_id === b.candidate_id
          || (a.interviewer_ids || []).some((x) => (b.interviewer_ids || []).includes(x))
          || (a.client_contact_email && a.client_contact_email === b.client_contact_email);
        if (shared) { ids.add(a.id); ids.add(b.id); }
      }
    }
    return ids;
  }, [rows]);

  const resend = async (iv) => {
    try { const r = await interviewsV2.resendInvite(iv.id); toast.success(`Invite re-sent (${Object.values(r.email.results || {}).join(', ')})`); }
    catch (e) { toast.error(errorText(e.message)); }
  };
  const openDetail = async (iv) => {
    try { setDetail(await interviewsV2.get(iv.id)); } catch (e) { toast.error(errorText(e.message)); }
  };

  const ModeIcon = ({ m }) => (m === 'in_person' ? <MapPin className="w-3.5 h-3.5" /> : m === 'phone' ? <Phone className="w-3.5 h-3.5" /> : <Video className="w-3.5 h-3.5" />);

  const Actions = ({ iv, compact }) => (
    <div className="flex flex-wrap gap-1 justify-end">
      {iv.status === 'scheduled' && (
        <>
          <Button size="sm" variant="outline" onClick={() => setReschedule(iv)} title="Reschedule"><RefreshCw className="w-3.5 h-3.5" />{!compact && <span className="ml-1">Reschedule</span>}</Button>
          <Button size="sm" variant="outline" onClick={() => { setOutcomePreset('pending'); setOutcomeFor(iv); }} title="Record outcome"><CheckCircle2 className="w-3.5 h-3.5" />{!compact && <span className="ml-1">Outcome</span>}</Button>
          <Button size="sm" variant="outline" onClick={() => { setOutcomePreset('no_show'); setOutcomeFor(iv); }} title="No-show"><UserX className="w-3.5 h-3.5" /></Button>
          <Button size="sm" variant="outline" onClick={() => resend(iv)} title="Re-send invite"><Mail className="w-3.5 h-3.5" /></Button>
          <Button size="sm" variant="outline" onClick={() => interviewsV2.downloadIcs(iv.id).catch((e) => toast.error(e.message))} title="Download .ics"><Download className="w-3.5 h-3.5" /></Button>
          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => setCancelFor(iv)} title="Cancel"><XCircle className="w-3.5 h-3.5" /></Button>
        </>
      )}
      {iv.status === 'no_show' && <Button size="sm" variant="outline" onClick={() => setReschedule(iv)}><RefreshCw className="w-3.5 h-3.5 mr-1" />Reschedule</Button>}
      <Button size="sm" variant="ghost" onClick={() => openDetail(iv)} title="Timeline"><Clock className="w-3.5 h-3.5" /></Button>
    </div>
  );

  const Row = ({ iv }) => (
    <div className={`rounded-lg border p-3 bg-white ${clashes.has(iv.id) ? 'border-amber-400' : 'border-slate-200'}`} data-testid="iv2-row">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <Link to={`/candidates/${iv.candidate_id}`} className="font-semibold text-slate-900 hover:underline">{iv.candidate_name}</Link>
            <StatusPill interview={iv} />
            <ModePill mode={iv.mode} />
            {clashes.has(iv.id) && <span className="inline-flex items-center gap-1 text-xs text-amber-700"><AlertTriangle className="w-3.5 h-3.5" /> clash</span>}
          </div>
          <div className="text-sm text-slate-700 mt-1 flex items-center gap-1">
            <ModeIcon m={iv.mode} /> <span className="font-medium">{iv.when_ist || fmtIst(iv.scheduled_at)}</span>
            <span className="text-slate-400">- {iv.duration_minutes || 45} min</span>
          </div>
          <div className="text-sm text-slate-600">
            {iv.round_name}{iv.client_name ? ` @ ${iv.client_name}` : ''}{iv.position_title ? ` - ${iv.position_title}` : ''}
          </div>
          <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-x-3">
            {iv.client_contact_name && <span>Contact: {iv.client_contact_name}</span>}
            {iv.interviewer_names?.length > 0 && <span>Panel: {iv.interviewer_names.join(', ')}</span>}
            {iv.recruiter_name && <span>Recruiter: {iv.recruiter_name}</span>}
            {iv.mode === 'video' && iv.meeting_link && iv.status === 'scheduled' && <a href={iv.meeting_link} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-0.5">Join <ExternalLink className="w-3 h-3" /></a>}
            {iv.google_calendar_link && <a href={iv.google_calendar_link} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">Google Calendar</a>}
          </div>
          {iv.outcome_notes && <p className="text-xs text-slate-600 mt-1 italic">"{iv.outcome_notes}"</p>}
          {iv.cancel_reason && <p className="text-xs text-slate-500 mt-1">Cancelled: {iv.cancel_reason}</p>}
        </div>
        <Actions iv={iv} />
      </div>
    </div>
  );

  const days = Array.from({ length: 7 }, (_, i) => new Date(weekStart.getTime() + i * DAY_MS));
  const todayKey = toIstParts(new Date().toISOString()).date;

  return (
    <div className="min-h-screen bg-slate-50">
      <Header title="Interviews" subtitle="Schedule, remind, reschedule, record the outcome" />
      <div className="p-6 max-w-7xl mx-auto space-y-4">
        {/* KPI strip */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            {[['Today', summary.today], ['Upcoming', summary.upcoming], ['Awaiting outcome', summary.awaiting_outcome, summary.awaiting_outcome > 0 ? 'text-rose-600' : ''],
              ['Completed (30d)', summary.completed_30d], ['No-shows (30d)', summary.no_show_30d], ['Reminders queued', summary.pending_reminders]].map(([l, v, cls]) => (
              <Card key={l}><CardContent className="p-3"><div className="text-xs text-slate-500">{l}</div><div className={`text-2xl font-semibold ${cls || 'text-slate-900'}`}>{v ?? 0}</div></CardContent></Card>
            ))}
          </div>
        )}
        {status && (
          <div className="text-xs text-slate-500 flex flex-wrap gap-4">
            <span>Email: {status.smtp_configured ? 'SMTP ready (.ics invites)' : <span className="text-red-600">SMTP not configured - invites will not go out</span>}</span>
            <span>Calendar: {status.google_calendar === 'connected' ? `Google (${status.google_calendar_email})` : '.ics only - connect Google Calendar in Settings > Calendar to also write your calendar'}</span>
            <span>WhatsApp: {status.whatsapp_approved?.dcs_interview_candidate ? 'candidate template approved' : <span className="text-amber-700">interview templates await owner approval - sends are refused</span>}</span>
            <span className="inline-flex items-center gap-1"><Bell className="w-3 h-3" /> Reminder thread: {status.scheduler?.alive ? `alive (last tick ${status.scheduler.last_tick ? fmtIst(status.scheduler.last_tick) : 'pending'})` : 'not running'}</span>
          </div>
        )}

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2">
          <Tabs value={layout === 'week' ? 'week' : view} onValueChange={(v) => { if (v === 'week') setLayout('week'); else { setLayout('list'); setView(v); } }}>
            <TabsList>
              {VIEWS.map((v) => <TabsTrigger key={v.key} value={v.key}>{v.label}</TabsTrigger>)}
              <TabsTrigger value="week"><Calendar className="w-3.5 h-3.5 mr-1" />Week</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
            <Input className="pl-8 w-56" placeholder="Candidate, client, role..." value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <Select value={mode} onValueChange={setMode}>
            <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All modes</SelectItem>{Object.entries(MODE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={recruiter} onValueChange={setRecruiter}>
            <SelectTrigger className="w-44"><SelectValue placeholder="Recruiter" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All recruiters</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={refresh}><RefreshCw className="w-3.5 h-3.5" /></Button>
          <div className="flex-1" />
          <Input className="w-56" placeholder="Candidate ID to schedule for" value={pickCandidate} onChange={(e) => setPickCandidate(e.target.value)} />
          <Button onClick={() => { if (!pickCandidate.trim()) { toast.info('Paste a candidate ID, or schedule from the candidate page / pipeline'); return; } setScheduleOpen(true); }} className="bg-emerald-600 hover:bg-emerald-700 text-white">
            <CalendarPlus className="w-4 h-4 mr-1" /> Schedule
          </Button>
        </div>

        {layout === 'week' ? (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Button size="sm" variant="outline" onClick={() => setWeekStart(new Date(weekStart.getTime() - 7 * DAY_MS))}><ChevronLeft className="w-4 h-4" /></Button>
              <Button size="sm" variant="outline" onClick={() => setWeekStart(startOfIstWeek(new Date()))}>This week</Button>
              <Button size="sm" variant="outline" onClick={() => setWeekStart(new Date(weekStart.getTime() + 7 * DAY_MS))}><ChevronRight className="w-4 h-4" /></Button>
              <span className="text-sm text-slate-600 ml-2">{fmtIst(days[0].toISOString(), { hour: undefined, minute: undefined })} - {fmtIst(days[6].toISOString(), { hour: undefined, minute: undefined })} ({rows.length} interview{rows.length === 1 ? '' : 's'})</span>
              <Button size="sm" variant="ghost" onClick={() => setLayout('list')}><List className="w-4 h-4 mr-1" />List</Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-7 gap-2">
              {days.map((d) => {
                const key = toIstParts(d.toISOString()).date;
                const items = byDay[key] || [];
                return (
                  <div key={key} className={`rounded-lg border p-2 min-h-[160px] ${key === todayKey ? 'border-emerald-400 bg-emerald-50/40' : 'border-slate-200 bg-white'}`}>
                    <div className="text-xs font-semibold text-slate-600 mb-1">{new Date(d.getTime() + 12 * 3600000).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', weekday: 'short', day: '2-digit', month: 'short' })}</div>
                    {items.length === 0 && <div className="text-[11px] text-slate-300">-</div>}
                    {items.map((iv) => (
                      <button key={iv.id} type="button" onClick={() => openDetail(iv)}
                        className={`w-full text-left rounded-md border px-2 py-1 mb-1 text-xs ${iv.status === 'cancelled' ? 'line-through opacity-50' : ''} ${clashes.has(iv.id) ? 'border-amber-400 bg-amber-50' : 'border-slate-200 bg-slate-50 hover:bg-slate-100'}`}>
                        <div className="font-medium truncate">{fmtIstTime(iv.scheduled_at)} {iv.candidate_name}</div>
                        <div className="text-slate-500 truncate">{iv.client_name || iv.round_name} - {MODE_LABEL[iv.mode] || iv.mode}</div>
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {loading && <p className="text-sm text-slate-400">Loading...</p>}
            {!loading && rows.length === 0 && (
              <Card><CardContent className="p-8 text-center text-slate-500">
                {view === 'today' ? 'Nothing scheduled for today.' : view === 'upcoming' ? 'No upcoming interviews. Schedule one from a candidate page or the requirement pipeline.' : 'Nothing here.'}
              </CardContent></Card>
            )}
            {Object.keys(byDay).sort((a, b) => (view === 'past' ? b.localeCompare(a) : a.localeCompare(b))).map((day) => (
              <div key={day}>
                <div className={`text-xs font-semibold uppercase tracking-wide mt-3 mb-1 ${day === todayKey ? 'text-emerald-700' : 'text-slate-500'}`}>
                  {new Date(`${day}T12:00:00+05:30`).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}{day === todayKey ? ' - today' : ''}
                </div>
                <div className="space-y-2">{byDay[day].map((iv) => <Row key={iv.id} iv={iv} />)}</div>
              </div>
            ))}
            {total > rows.length && <p className="text-xs text-slate-400">Showing {rows.length} of {total}.</p>}
          </div>
        )}
      </div>

      {/* Dialogs */}
      <ScheduleInterviewDialog open={scheduleOpen} onOpenChange={setScheduleOpen} candidate={{ id: pickCandidate.trim(), name: 'candidate' }} onSaved={refresh} />
      <ScheduleInterviewDialog open={!!reschedule} onOpenChange={(v) => { if (!v) setReschedule(null); }} candidate={reschedule ? { id: reschedule.candidate_id, name: reschedule.candidate_name } : null} interview={reschedule} onSaved={refresh} />
      <OutcomeDialog open={!!outcomeFor} onOpenChange={(v) => { if (!v) setOutcomeFor(null); }} interview={outcomeFor} presetOutcome={outcomePreset} onDone={refresh} />
      <CancelDialog open={!!cancelFor} onOpenChange={(v) => { if (!v) setCancelFor(null); }} interview={cancelFor} onDone={refresh} />

      {detail && (
        <div className="fixed inset-0 z-50 bg-black/30 flex justify-end" onClick={() => setDetail(null)}>
          <div className="w-full max-w-md h-full bg-white shadow-xl overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-slate-900">{detail.interview.candidate_name} - {detail.interview.round_name}</h3>
              <Button size="sm" variant="ghost" onClick={() => setDetail(null)}>Close</Button>
            </div>
            <div className="text-sm text-slate-600 mb-3">{detail.interview.when_ist} - {MODE_LABEL[detail.interview.mode]} - <StatusPill interview={detail.interview} /></div>
            <h4 className="text-xs font-semibold uppercase text-slate-500 mb-1">Reminders</h4>
            <ul className="text-xs space-y-1 mb-4">
              {detail.reminders.length === 0 && <li className="text-slate-400">none</li>}
              {detail.reminders.map((r) => (
                <li key={r.id} className="flex justify-between gap-2">
                  <span>{r.audience} - {r.channel} - {r.offset_minutes >= 60 ? `${r.offset_minutes / 60}h` : `${r.offset_minutes}m`} before</span>
                  <span className={r.status === 'sent' ? 'text-emerald-700' : r.status === 'pending' ? 'text-blue-700' : 'text-slate-400'}>{r.status}{r.status === 'pending' ? ` (${fmtIst(r.due_at)})` : ''}</span>
                </li>
              ))}
            </ul>
            <h4 className="text-xs font-semibold uppercase text-slate-500 mb-1">Timeline</h4>
            <ul className="text-xs space-y-1">
              {detail.events.map((e) => (
                <li key={e.id}><span className="text-slate-400">{fmtIst(e.at)}</span> - <span className="font-medium">{e.kind}</span> <span className="text-slate-500">{e.actor_name}</span>{e.data && e.data.reason ? ` - ${e.data.reason}` : ''}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
